/* ============================================================
   MULEN — experience controller
   Loads vendor libs, boots Lenis smooth scroll + GSAP ScrollTrigger,
   wires native Shopify cart AJAX, drawers, header, reveals.
   No framework. Progressive + capability-aware.
   ============================================================ */
(function () {
  'use strict';

  const cfg = (window.MULEN = window.MULEN || {});
  const S = cfg.settings || {};
  const A = cfg.assets || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.matchMedia('(max-width: 860px)').matches;
  const coarse = window.matchMedia('(hover: none)').matches;

  /* ---------- tiny helpers ---------- */
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const on = (el, ev, fn, o) => el && el.addEventListener(ev, fn, o);
  const loadScript = (src) =>
    new Promise((res, rej) => {
      if (!src) return rej(new Error('no src'));
      if (document.querySelector(`script[src="${src}"]`)) return res();
      const s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = res; s.onerror = () => rej(new Error('failed ' + src));
      document.head.appendChild(s);
    });

  function formatMoney(cents) {
    const fmt = S.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    const noDec = Math.round(cents / 100).toLocaleString();
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount);
  }
  cfg.formatMoney = formatMoney;

  /* ============================================================
     1. SMOOTH SCROLL (Lenis) + GSAP ScrollTrigger bridge
     ============================================================ */
  let lenis = null;
  const scrollState = { y: 0, velocity: 0 };
  cfg.scrollState = scrollState;
  const lenisSubscribers = new Set();
  cfg.onScroll = (fn) => { lenisSubscribers.add(fn); return () => lenisSubscribers.delete(fn); };

  async function bootMotion() {
    let gsap = null, ScrollTrigger = null;
    try {
      await loadScript(A.gsap);
      await loadScript(A.scrollTrigger);
      gsap = window.gsap;
      ScrollTrigger = window.ScrollTrigger;
      if (gsap && ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
      cfg.gsap = gsap; cfg.ScrollTrigger = ScrollTrigger;
    } catch (e) { /* reveals fall back to IntersectionObserver below */ }

    if (S.smoothScroll && !reduceMotion) {
      try {
        await loadScript(A.lenis);
        if (window.Lenis) {
          lenis = new window.Lenis({
            duration: isMobile ? 0.9 : 1.15,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
            syncTouch: false, // native touch scroll on mobile = best perf
          });
          cfg.lenis = lenis;
          lenis.on('scroll', (e) => {
            scrollState.y = e.scroll;
            scrollState.velocity = e.velocity;
            if (ScrollTrigger) ScrollTrigger.update();
            lenisSubscribers.forEach((fn) => fn(scrollState));
          });
          if (gsap) {
            gsap.ticker.add((time) => lenis.raf(time * 1000));
            gsap.ticker.lagSmoothing(0);
          } else {
            const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
            requestAnimationFrame(raf);
          }
        }
      } catch (e) { /* native scroll fallback */ }
    }
    if (!lenis) {
      // keep scrollState fed for parallax even without Lenis
      const fallback = () => {
        scrollState.y = window.scrollY;
        lenisSubscribers.forEach((fn) => fn(scrollState));
      };
      on(window, 'scroll', fallback, { passive: true });
    }
    initReveals();
    initParallax();
    document.dispatchEvent(new CustomEvent('mulen:motion-ready', { detail: { gsap, ScrollTrigger, lenis } }));
  }

  /* ============================================================
     2. REVEALS — GSAP ScrollTrigger if present, else IO fallback
     ============================================================ */
  function initReveals() {
    const items = $$('[data-reveal], [data-reveal-stagger]');
    if (!items.length) return;
    if (reduceMotion) { items.forEach((el) => el.classList.add('is-in')); return; }

    const ST = cfg.ScrollTrigger;
    if (ST) {
      items.forEach((el) => {
        ST.create({
          trigger: el,
          start: 'top 88%',
          once: true,
          onEnter: () => el.classList.add('is-in'),
        });
      });
      if (cfg.gsap) {
        $$('[data-reveal-stagger]').forEach((wrap) => {
          const kids = Array.from(wrap.children);
          ST.create({
            trigger: wrap, start: 'top 86%', once: true,
            onEnter: () => cfg.gsap.to(kids, { opacity: 1, y: 0, duration: 0.8, stagger: 0.08, ease: 'power3.out' }),
          });
        });
      }
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
      }, { rootMargin: '0px 0px -12% 0px' });
      items.forEach((el) => io.observe(el));
    }
  }

  /* ============================================================
     2b. PARALLAX — subtle image drift tied to scroll
     ============================================================ */
  function initParallax() {
    if (reduceMotion) return;
    const els = $$('[data-parallax], [data-parallax-soft]').map((el) => ({
      el,
      img: el.matches('[data-parallax]') ? (el.querySelector('img') || el) : el,
      strength: el.matches('[data-parallax-soft]') ? 0.04 : 0.12,
    }));
    if (!els.length) return;
    let ticking = false;
    function apply() {
      ticking = false;
      const vh = window.innerHeight;
      els.forEach((p) => {
        const r = p.el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const center = r.top + r.height / 2 - vh / 2;
        const shift = -center * p.strength * (S.motion || 0.8);
        p.img.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0) scale(1.06)`;
      });
    }
    const req = () => { if (!ticking) { ticking = true; requestAnimationFrame(apply); } };
    cfg.onScroll(req);
    on(window, 'resize', req);
    apply();
  }

  /* ============================================================
     3. HEADER — hide on scroll down, show on up
     ============================================================ */
  function initHeader() {
    const header = $('.header');
    if (!header) return;
    const startsTransparent = header.dataset.transparent === 'true';
    let last = 0;
    cfg.onScroll((st) => {
      const y = st.y;
      // hide on scroll down, show on scroll up
      if (y > last && y > 220) header.classList.add('header--hidden');
      else header.classList.remove('header--hidden');
      last = y;
      // solidify transparent header once past the hero
      if (startsTransparent) {
        if (y > window.innerHeight * 0.7) header.classList.remove('header--transparent');
        else header.classList.add('header--transparent');
      }
    });
  }

  /* ============================================================
     4. DRAWERS — cart, mobile nav, search
     ============================================================ */
  function lockScroll(lock) {
    if (lenis) { lock ? lenis.stop() : lenis.start(); }
    document.documentElement.classList.toggle('lenis-stopped', lock);
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  const Drawer = {
    open(el, overlay) {
      el && el.classList.add('is-open');
      overlay && overlay.classList.add('is-open');
      el && el.setAttribute('aria-hidden', 'false');
      lockScroll(true);
    },
    close(el, overlay) {
      el && el.classList.remove('is-open');
      overlay && overlay.classList.remove('is-open');
      el && el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
    },
  };
  cfg.Drawer = Drawer;

  function initDrawers() {
    const cart = $('#CartDrawer');
    const overlay = $('#DrawerOverlay');
    const nav = $('#NavDrawer');
    const search = $('#SearchModal');

    cfg.openCart = () => Drawer.open(cart, overlay);
    cfg.closeCart = () => Drawer.close(cart, overlay);

    $$('[data-cart-toggle]').forEach((b) => on(b, 'click', (e) => { e.preventDefault(); cfg.openCart(); }));
    $$('[data-cart-close]').forEach((b) => on(b, 'click', () => cfg.closeCart()));
    on(overlay, 'click', () => { cfg.closeCart(); });

    $$('[data-nav-toggle]').forEach((b) => on(b, 'click', (e) => { e.preventDefault(); Drawer.open(nav); }));
    $$('[data-nav-close]').forEach((b) => on(b, 'click', () => Drawer.close(nav)));
    $$('#NavDrawer a').forEach((a) => on(a, 'click', () => Drawer.close(nav)));

    $$('[data-search-toggle]').forEach((b) => on(b, 'click', (e) => {
      e.preventDefault(); Drawer.open(search); const inp = search && search.querySelector('input[type="search"]'); inp && setTimeout(() => inp.focus(), 350);
    }));
    $$('[data-search-close]').forEach((b) => on(b, 'click', () => Drawer.close(search)));

    on(document, 'keydown', (e) => {
      if (e.key === 'Escape') {
        cfg.closeCart();
        nav && Drawer.close(nav);
        search && Drawer.close(search);
      }
    });
  }

  /* ============================================================
     5. CART — native Shopify AJAX API
     ============================================================ */
  const Cart = {
    async getState() {
      const r = await fetch(`${cfg.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      return r.json();
    },
    async add(items) {
      const r = await fetch(cfg.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw err; }
      return r.json();
    },
    async change(line, quantity) {
      const r = await fetch(cfg.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      return r.json();
    },
  };
  cfg.Cart = Cart;

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  async function renderCartDrawer(state) {
    const body = $('#CartDrawerBody');
    if (!body) return;
    updateCartCount(state.item_count);
    if (state.item_count === 0) {
      body.innerHTML = `<div class="cart-drawer__empty">
        <p class="h-md">${(cfg.strings && cfg.strings.empty) || 'Your cart is empty'}</p>
        <a href="/collections/all" class="btn" data-cart-close>Continue shopping</a></div>`;
      $$('[data-cart-close]', body).forEach((b) => on(b, 'click', () => cfg.closeCart()));
      const foot = $('#CartDrawerFoot'); if (foot) foot.hidden = true;
      return;
    }
    const lines = state.items.map((it, i) => `
      <div class="cart-line" data-line="${i + 1}">
        <img class="cart-line__img" src="${it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x150$1$2') : ''}" alt="" loading="lazy">
        <div>
          <a class="cart-line__title" href="${it.url}">${it.product_title}</a>
          ${it.variant_title && it.variant_title !== 'Default Title' ? `<div class="cart-line__variant">${it.variant_title}</div>` : ''}
          <div class="cart-line__qty">
            <button data-qty-down aria-label="Decrease">&minus;</button>
            <span data-qty>${it.quantity}</span>
            <button data-qty-up aria-label="Increase">+</button>
          </div>
          <button class="cart-line__remove" data-remove>Remove</button>
        </div>
        <div class="cart-line__price">${formatMoney(it.final_line_price)}</div>
      </div>`).join('');
    body.innerHTML = lines;

    const foot = $('#CartDrawerFoot');
    if (foot) { foot.hidden = false; const sub = $('#CartSubtotal'); if (sub) sub.textContent = formatMoney(state.total_price); }

    // AOV: load product recommendations based on first item
    if (state.items[0]) loadRecommendations(state.items[0].product_id);

    $$('.cart-line', body).forEach((row) => {
      const line = parseInt(row.dataset.line, 10);
      const qtyEl = $('[data-qty]', row);
      const q = parseInt(qtyEl.textContent, 10);
      on($('[data-qty-up]', row), 'click', () => mutate(line, q + 1));
      on($('[data-qty-down]', row), 'click', () => mutate(line, q - 1));
      on($('[data-remove]', row), 'click', () => mutate(line, 0));
    });

    async function mutate(line, quantity) {
      body.classList.add('is-loading');
      const state = await Cart.change(line, Math.max(0, quantity));
      body.classList.remove('is-loading');
      renderCartDrawer(state);
    }
  }
  cfg.renderCartDrawer = renderCartDrawer;
  cfg.refreshCart = async () => renderCartDrawer(await Cart.getState());

  /* ---- AOV: cart drawer product recommendations ---- */
  let recsCache = {};
  async function loadRecommendations(productId) {
    const wrap = $('#CartRecs');
    const list = $('#CartRecsList');
    if (!wrap || !list || !productId) return;
    try {
      let products = recsCache[productId];
      if (!products) {
        const r = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6&intent=related`, { headers: { Accept: 'application/json' } });
        const data = await r.json();
        products = (data && data.products) || [];
        recsCache[productId] = products;
      }
      // exclude items already in cart
      const inCart = new Set((await Cart.getState()).items.map((i) => i.product_id));
      products = products.filter((p) => !inCart.has(p.id)).slice(0, 6);
      if (!products.length) { wrap.hidden = true; return; }
      list.innerHTML = products.map((p) => {
        const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
        const img = (p.featured_image || (p.images && p.images[0]) || '');
        const imgUrl = typeof img === 'string' ? img : (img && img.src) || '';
        return `<div class="cart-rec">
          <a href="${p.url}"><img src="${imgUrl ? imgUrl.replace(/(\.[a-z]+)(\?|$)/i, '_240x300$1$2') : ''}" alt="" loading="lazy" width="120" height="150"></a>
          <a class="cart-rec__t" href="${p.url}">${p.title}</a>
          <span class="cart-rec__p">${formatMoney(p.price)}</span>
          ${v ? `<button type="button" class="cart-rec__add" data-rec-add="${v.id}">+ Add</button>` : ''}
        </div>`;
      }).join('');
      wrap.hidden = false;
      $$('[data-rec-add]', list).forEach((b) => on(b, 'click', async () => {
        b.textContent = '…'; b.disabled = true;
        try { await Cart.add([{ id: b.dataset.recAdd, quantity: 1 }]); await cfg.refreshCart(); }
        catch (e) { b.textContent = '+ Add'; b.disabled = false; }
      }));
    } catch (e) { wrap.hidden = true; }
  }

  function initAddToCart() {
    $$('[data-product-form]').forEach((form) => {
      on(form, 'submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const id = form.querySelector('[name="id"]');
        if (!id || !id.value) return;
        const orig = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = '…'; }
        try {
          const qty = form.querySelector('[name="quantity"]');
          await Cart.add([{ id: id.value, quantity: qty ? parseInt(qty.value, 10) || 1 : 1 }]);
          const state = await Cart.getState();
          await renderCartDrawer(state);
          cfg.openCart();
          if (btn) btn.textContent = cfg.strings.addedToCart || 'Added';
        } catch (err) {
          if (btn) btn.textContent = (err && err.description) || 'Error';
        } finally {
          setTimeout(() => { if (btn) { btn.disabled = false; btn.textContent = orig; } }, 1400);
        }
      });
    });
  }

  /* ============================================================
     6. LAZY-LOAD WebGL modules per page
     ============================================================ */
  async function initWebGL() {
    if (!S.webgl || reduceMotion) return;
    // Don't pay the WebGL cost on tiny/low-memory devices
    const lowEnd = navigator.deviceMemory && navigator.deviceMemory <= 2;
    const heroEl = $('[data-hero-3d]');
    const fxGrid = $('[data-grid-fx]');
    if (!heroEl && !fxGrid) return;

    let three = false;
    try { await loadScript(A.three); three = !!window.THREE; } catch (e) { three = false; }
    if (!three) return; // graceful: static fallbacks already visible

    if (heroEl && A.hero3d) {
      try { await loadScript(A.hero3d); window.MulenHero3D && window.MulenHero3D.init(heroEl, { THREE: window.THREE, lowEnd, isMobile, coarse }); } catch (e) {}
    }
    if (fxGrid && A.gridFx && !lowEnd && !coarse) {
      try { await loadScript(A.gridFx); window.MulenGridFx && window.MulenGridFx.init(fxGrid, { THREE: window.THREE, isMobile }); } catch (e) {}
    }
  }

  /* ============================================================
     boot
     ============================================================ */
  function boot() {
    initDrawers();
    initHeader();
    initAddToCart();
    bootMotion();          // async — also calls initReveals when ready
    initWebGL();           // async, lazy
    // header reveal needs scroll subscription which exists immediately
    document.addEventListener('mulen:request-cart', () => cfg.refreshCart());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
